import { useCallback } from 'react';
import { useMatch, useNavigate, useParams } from 'react-router-dom';

import { Badge, Button, InformationBox } from '@pukka/ui-components';
import { useSnapshot } from 'valtio';

import { RefundButton, TransactionRefundModal, TransactionRefundState } from './Refund';
import { ResendReceiptModal } from './ResendReceiptModal';
import { TAB } from './Transaction.state';
import {
  TransactionDetailState,
  onResendReceipt,
  onShowReceipt,
  onToggleResendReceipt,
  useTransactionDetail,
} from './TransactionDetail.state';
import { TransactionDetailSection, TransactionDetailTable } from './TransactionDetailSection';
import { getBadgeColor, getBadgeText } from './utils';

interface TransactionDetailPageletProps {
  readonly State?: typeof TransactionDetailState;
}

function TransactionDetailPagelet({ State = TransactionDetailState }: TransactionDetailPageletProps) {
  const { id, token } = useParams();
  const match = useMatch('v2/reports/transactions/:tabId/:id?/:token?');
  const navigate = useNavigate();
  const currentTabId = match?.params.tabId as TAB;
  const {
    details,
    items,
    customer,
    receiptUrl,
    voids,
    refunded,
    refundedLength,
    status,
    resendReceiptStatus,
    shouldShowResendReceiptModal,
    hasBeenReprinted,
  } = useSnapshot(State);
  const { showModal: showRefundModal, transactionDetail } = useSnapshot(TransactionRefundState);

  const onCloseDetail = useCallback(() => {
    navigate(`/v2/reports/transactions/${currentTabId}`, {
      unstable_viewTransition: true,
      preventScrollReset: true,
    });
  }, [navigate, currentTabId]);

  useTransactionDetail(currentTabId, id as string, token as string);

  return (
    <>
      <ResendReceiptModal
        open={shouldShowResendReceiptModal}
        loading={resendReceiptStatus === 'loading'}
        onToggleModal={onToggleResendReceipt}
        onSend={onResendReceipt}
      />
      {showRefundModal && transactionDetail ? <TransactionRefundModal onCloseDetail={onCloseDetail} /> : null}
      <div className="sticky top-4 ml-4 flex max-h-[calc(100dvh_-_1rem)] flex-1 flex-col view-transition-[tx-detail]">
        <div className="h-1 flex-1 flex-col overflow-auto rounded-t-xl bg-neutral-50 px-5 pb-80 pt-4 md:overscroll-y-none">
          {currentTabId === 'success' && hasBeenReprinted && (
            <InformationBox title="This receipt has been reprinted" variant="secondary" icon="info" className="mb-4" />
          )}
          <TransactionDetailSection title="ORDER DETAILS">
            <TransactionDetailSection.Row title="Status">
              <Badge color={getBadgeColor(currentTabId)} text={getBadgeText(currentTabId)} />
            </TransactionDetailSection.Row>
            {details.map(([title, value]) => {
              return (
                <TransactionDetailSection.Row loading={status === 'loading'} title={title} key={title}>
                  {value}
                </TransactionDetailSection.Row>
              );
            })}
          </TransactionDetailSection>
          <TransactionDetailSection title="ORDERED ITEMS">
            <TransactionDetailTable
              columnCount={2}
              header={
                <TransactionDetailTable.Row>
                  <TransactionDetailTable.Header>Items</TransactionDetailTable.Header>
                  <TransactionDetailTable.Header>Quantity</TransactionDetailTable.Header>
                </TransactionDetailTable.Row>
              }
            >
              {items.map(([title, value]) => (
                <TransactionDetailTable.Row className="text-neutral-400" key={title}>
                  <TransactionDetailTable.Cell>{title}</TransactionDetailTable.Cell>
                  <TransactionDetailTable.Cell>{value}</TransactionDetailTable.Cell>
                </TransactionDetailTable.Row>
              ))}
            </TransactionDetailTable>
          </TransactionDetailSection>
          <TransactionDetailSection title="VOIDED ITEMS">
            <TransactionDetailTable
              columnCount={4}
              header={
                <TransactionDetailTable.Row>
                  <TransactionDetailTable.Header>Items</TransactionDetailTable.Header>
                  <TransactionDetailTable.Header>Qty</TransactionDetailTable.Header>
                  <TransactionDetailTable.Header>Reason</TransactionDetailTable.Header>
                  <TransactionDetailTable.Header>Executed by</TransactionDetailTable.Header>
                </TransactionDetailTable.Row>
              }
            >
              {voids.map(([title, value, reason, name]) => (
                <TransactionDetailTable.Row className="text-neutral-400" key={title}>
                  <TransactionDetailTable.Cell width="25%">{title}</TransactionDetailTable.Cell>
                  <TransactionDetailTable.Cell width="25%">{value}</TransactionDetailTable.Cell>
                  <TransactionDetailTable.Cell width="25%">{reason}</TransactionDetailTable.Cell>
                  <TransactionDetailTable.Cell width="25%">{name}</TransactionDetailTable.Cell>
                </TransactionDetailTable.Row>
              ))}
            </TransactionDetailTable>
          </TransactionDetailSection>
          {currentTabId === 'success' && refundedLength > 0 && (
            <TransactionDetailSection title="REFUNDED">
              <TransactionDetailTable
                columnCount={5}
                header={
                  <TransactionDetailTable.Row>
                    <TransactionDetailTable.Header>Items</TransactionDetailTable.Header>
                    <TransactionDetailTable.Header>Qty</TransactionDetailTable.Header>
                    <TransactionDetailTable.Header>Reason</TransactionDetailTable.Header>
                    <TransactionDetailTable.Header>Executed by</TransactionDetailTable.Header>
                    <TransactionDetailTable.Header>Date and Time</TransactionDetailTable.Header>
                  </TransactionDetailTable.Row>
                }
              >
                {refunded.map(([itemName, qty, reason, executedBy, date], i) => (
                  <TransactionDetailTable.Row className="text-neutral-400" key={i}>
                    <TransactionDetailTable.Cell width="20%">{itemName}</TransactionDetailTable.Cell>
                    <TransactionDetailTable.Cell width="5%">{qty}</TransactionDetailTable.Cell>
                    <TransactionDetailTable.Cell width="25%">{reason}</TransactionDetailTable.Cell>
                    <TransactionDetailTable.Cell width="25%">{executedBy}</TransactionDetailTable.Cell>
                    <TransactionDetailTable.Cell width="25%">{date}</TransactionDetailTable.Cell>
                  </TransactionDetailTable.Row>
                ))}
              </TransactionDetailTable>
            </TransactionDetailSection>
          )}
          {currentTabId === 'success' && customer.length ? (
            <TransactionDetailSection title="CUSTOMER DETAILS">
              {customer.map(([title, value, usePadding]) => (
                <TransactionDetailSection.Row loading={status === 'loading'} title={title} key={title} className={usePadding ? 'ml-3' : ''}>
                  {value}
                </TransactionDetailSection.Row>
              ))}
            </TransactionDetailSection>
          ) : null}
        </div>
        <div className="sticky bottom-0 bg-white">
          <div className="mb-4 flex flex-row justify-between gap-2.5 rounded-b-xl bg-neutral-200 px-5 py-2.5">
            <Button size="m" onClick={onCloseDetail}>
              Close
            </Button>

            {currentTabId === 'success' ? (
              <div className="flex gap-2.5">
                <Button variant="secondary" size="m" onClick={() => onToggleResendReceipt(true)}>
                  Resend Receipt
                </Button>
                <RefundButton orderDetail={TransactionRefundState.transactionDetail} paymentMethods={State.paymentMethods} />
                {receiptUrl ? (
                  <Button variant="secondary" size="m" onClick={() => onShowReceipt(State)}>
                    Show Receipt
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export { TransactionDetailPagelet };
